import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// 主题模式：light 亮色 / dark 暗色 / system 跟随系统
export const THEME_LIGHT = 'light';
export const THEME_DARK = 'dark';
export const THEME_SYSTEM = 'system';

const STORAGE_KEY = 'app-theme-mode';

const ThemeContext = createContext({
  themeMode: THEME_SYSTEM,
  resolvedTheme: THEME_LIGHT,
  setThemeMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// 读取系统偏好
const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME_DARK : THEME_LIGHT;
  }
  return THEME_LIGHT;
};

// 读取本地存储的主题模式，无记录时默认跟随系统
const getStoredMode = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === THEME_LIGHT || stored === THEME_DARK || stored === THEME_SYSTEM) {
      return stored;
    }
  } catch (e) {
    // 忽略读取异常
  }
  return THEME_SYSTEM;
};

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(getStoredMode);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  // 监听系统主题变化
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemTheme(e.matches ? THEME_DARK : THEME_LIGHT);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // 实际生效的主题：system 时按系统偏好解析
  const resolvedTheme = themeMode === THEME_SYSTEM ? systemTheme : themeMode;

  // 将生效主题写入 documentElement，供 CSS 通过 [data-theme] 切换样式
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      // 忽略写入异常
    }
  }, []);

  const value = {
    themeMode,
    resolvedTheme,
    setThemeMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
